import React, { useEffect } from "react";
import Main from "./routes/Main";
import Loader from "./components/Loader";
import { useLoader } from "./context/LoaderContext";
import { tryRefreshToken } from "./api/RefreshTokenApi";
import { setAccessToken } from "./axioxClient.ts/axiosClient";
import { fetchProfile } from "./api/UserApi";
import { useUser } from "./context/UserContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchDropdownCategories,
  fetchRoles,
  fetchStatusManagementOption,
} from "./api/AdminApi";
import { dynamicFailed } from "./hooks/useToast";
import { useOption } from "./context/OptionContext";
import { useRole } from "./context/RoleContext";
import { useSettings } from "./context/SettingsContext";
import {
  fetchGeneralSettings,
  fetchNotificationSettings,
  fetchSecuritySettings,
} from "./api/SettingsApi";
// import { dynamicFailed } from "./hooks/useToast";

const MainPage = () => {
  const { setLoading } = useLoader();
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { setCategories, setStatusOptions } = useOption();
  const { setRoles } = useRole();
  const {
    generalSettings,
    setGeneralSettings,
    setNotificationSettings,
    setSecuritySettings,
  } = useSettings();

  useEffect(() => {
    const publicPaths = ["/login", "/reset-password"];
    if (publicPaths.includes(location.pathname)) {
      return;
    }
    if (user) {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
      return;
    }
    tryRefreshToken(navigate)
      .then(async (newAccessToken) => {
        setAccessToken(newAccessToken);
        await fetchProfile().then((userData) => {
          setUser(userData);
        });
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      })
      .catch(() => {
        // dynamicFailed("User must login again!");
        setUser(null);
        setLoading(false);
      });
  }, [location.pathname, navigate, setLoading, setUser, user]);

  const { pathname } = useLocation();

  useEffect(() => {
    setLoading(true);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [pathname, setLoading]);

  useEffect(() => {
    if (!user) {
      return;
    }
    Promise.all([
      fetchDropdownCategories(),
      fetchStatusManagementOption(),
      fetchRoles(),
    ])
      .then(([categoryData, statusData, roleData]) => {
        setCategories(categoryData);
        setStatusOptions(statusData);
        setRoles(roleData);
      })
      .catch((error) => {
        dynamicFailed(`${error.message}`);
      });
  }, [setCategories, setLoading, setRoles, setStatusOptions, user]);

  useEffect(() => {
    if (!user) {
      return;
    }
    Promise.all([
      fetchGeneralSettings(),
      fetchNotificationSettings(),
      fetchSecuritySettings(),
    ])
      .then(([generalData, notificationData, securityData]) => {
        if (generalData) {
          setGeneralSettings(generalData);
        }
        if (notificationData) {
          setNotificationSettings(notificationData);
        }
        if (securityData) {
          setSecuritySettings(securityData);
        }
      })
      .catch((error) => {
        dynamicFailed(`${error.message}`);
      });
  }, [
    setGeneralSettings,
    setLoading,
    setNotificationSettings,
    setSecuritySettings,
    user,
  ]);

  useEffect(() => {
    if (generalSettings?.websiteName) {
      document.title = generalSettings.websiteName;
    }

    if (!generalSettings?.documentUrl) return;

    let link = document.querySelector("link[rel~='icon']");

    if (link instanceof HTMLLinkElement) {
      link.href = `${process.env.REACT_APP_Url}/${generalSettings.documentUrl}`;
    } else {
      const newLink = document.createElement("link");
      newLink.rel = "icon";
      newLink.href = `${process.env.REACT_APP_Url}/${generalSettings.documentUrl}`;
      document.head.appendChild(newLink);
    }
  }, [generalSettings]);

  return (
    <>
      <Main />
      <Loader />
    </>
  );
};

export default MainPage;
